import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useNavigate, useParams, Link } from "react-router";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import { useCreateMenuItemMutation, useUpdateMenuItemMutation } from "@/lib/mutations";
import { useCategoriesQuery, useMenuQuery } from "@/lib/queries";

type MenuFormState = {
  category_id: string;
  name: string;
  description: string;
  price: string;
  unit: string;
  available: boolean;
  sort_order: number;
};

const initialForm: MenuFormState = {
  category_id: "",
  name: "",
  description: "",
  price: "",
  unit: "",
  available: true,
  sort_order: 0,
};

function findItemById(menu: any[], id: string | undefined) {
  if (!id) return null;

  for (const category of menu) {
    const item = category.items?.find((entry: any) => String(entry.id) === id);
    if (item) return item;
  }

  return null;
}

export function MenuItemForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState(initialForm);
  const didPopulateEditForm = useRef(false);

  const categoriesQuery = useCategoriesQuery();
  const menuQuery = useMenuQuery({ enabled: isEdit });
  const createMenuItemMutation = useCreateMenuItemMutation();
  const updateMenuItemMutation = useUpdateMenuItemMutation();

  const categories = categoriesQuery.data ?? [];
  const menu = menuQuery.data ?? [];
  const selectedItem = useMemo(() => findItemById(menu, id), [id, menu]);

  useEffect(() => {
    didPopulateEditForm.current = false;
    setForm(initialForm);
  }, [id]);

  useEffect(() => {
    if (!isEdit || !selectedItem || didPopulateEditForm.current) return;

    setForm({
      category_id: String(selectedItem.category_id),
      name: selectedItem.name,
      description: selectedItem.description,
      price: selectedItem.price,
      unit: selectedItem.unit ?? "",
      available: !!selectedItem.available,
      sort_order: selectedItem.sort_order,
    });
    didPopulateEditForm.current = true;
  }, [isEdit, selectedItem]);

  const isInitialLoading = categoriesQuery.isPending || (isEdit && menuQuery.isPending);
  const isSubmitting = createMenuItemMutation.isPending || updateMenuItemMutation.isPending;
  const queryError = categoriesQuery.error ?? menuQuery.error;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const data = {
      ...form,
      category_id: Number(form.category_id),
      available: form.available ? 1 : 0,
    };

    try {
      if (isEdit) {
        await updateMenuItemMutation.mutateAsync({ id: Number(id), data });
        toast.success("Item updated");
      } else {
        await createMenuItemMutation.mutateAsync(data);
        toast.success("Item created");
      }
      navigate("/menu");
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
    }
  };

  const inputClass =
    "flex h-10 w-full rounded-lg border bg-background px-3 text-[13px] shadow-sm transition-all placeholder:text-muted-foreground/50 focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground";

  if (isInitialLoading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-foreground border-t-transparent" />
      </div>
    );
  }

  if (queryError) {
    return (
      <div className="flex h-48 flex-col items-center justify-center gap-3 rounded-lg border">
        <p className="text-[13px] text-muted-foreground">{getErrorMessage(queryError)}</p>
        <button
          onClick={() => {
            categoriesQuery.refetch();
            if (isEdit) menuQuery.refetch();
          }}
          className="inline-flex h-8 items-center rounded-md border px-3 text-[12px] font-medium transition-colors hover:bg-muted"
        >
          Retry
        </button>
      </div>
    );
  }

  if (isEdit && !selectedItem) {
    return (
      <div className="flex h-48 items-center justify-center">
        <p className="text-[13px] text-muted-foreground">Menu item not found.</p>
      </div>
    );
  }

  return (
    <div className="max-w-lg space-y-8">
      <div>
        <Link
          to="/menu"
          className="mb-4 inline-flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-3" />
          Back to menu
        </Link>
        <h1 className="text-[22px] font-semibold tracking-tight">
          {isEdit ? "Edit item" : "New item"}
        </h1>
        <p className="mt-1 text-[13px] text-muted-foreground">
          {isEdit ? "Update this menu item." : "Add a new item to your menu."}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-muted-foreground">Category</label>
          <select
            value={form.category_id}
            onChange={(e) => setForm({ ...form, category_id: e.target.value })}
            required
            className={inputClass}
          >
            <option value="">Select category</option>
            {categories.map((c: any) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-muted-foreground">Name</label>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            placeholder="Sourdough Loaf"
            className={inputClass}
          />
        </div>

        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-muted-foreground">Description</label>
          <input
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="A brief description"
            className={inputClass}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-muted-foreground">Price</label>
            <input
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              required
              placeholder="$30"
              className={inputClass}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-muted-foreground">Unit</label>
            <input
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
              placeholder="e.g. order of ten"
              className={inputClass}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-[13px] font-medium text-muted-foreground">Sort order</label>
            <input
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
              className={inputClass}
            />
          </div>
          <div className="flex items-end pb-2">
            <label className="flex cursor-pointer items-center gap-2.5">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={form.available}
                  onChange={(e) => setForm({ ...form, available: e.target.checked })}
                  className="peer sr-only"
                />
                <div className="h-5 w-9 rounded-full bg-muted transition-colors peer-checked:bg-foreground" />
                <div className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-background shadow-sm transition-transform peer-checked:translate-x-4" />
              </div>
              <span className="text-[13px] font-medium">Available</span>
            </label>
          </div>
        </div>

        <div className="flex gap-3 pt-3">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex h-9 items-center rounded-lg bg-foreground px-4 text-[13px] font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {isSubmitting ? (
              <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-background border-t-transparent" />
            ) : (
              isEdit ? "Save changes" : "Create item"
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate("/menu")}
            disabled={isSubmitting}
            className="inline-flex h-9 items-center rounded-lg border px-4 text-[13px] font-medium transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
